import { inject, Service } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { AppHttp } from '@core/http/http.service';
import { ApiError } from '@core/http/api-error';
import { LoggerService, ScopedLogger } from '@core/logging/logger.service';
import { AutoRenewRequestDto } from './dtos/requests/auto-renew-dto-request';
import { AutoRenewResultDto } from './dtos/responses/auto-renew-result-dto-response';
import { CancelRequestDto } from './dtos/requests/cancel-dto-request';
import { CancelResultDto } from './dtos/responses/cancel-result-dto-response';
import { CardDto } from './dtos/responses/card-dto-response';
import { InvoiceDto } from './dtos/responses/invoice-dto-response';
import { PaymentMethodRequestDto } from './dtos/requests/payment-method-dto-request';
import { PaymentMethodResultDto } from './dtos/responses/payment-method-result-dto-response';
import { QuoteResultDto } from './dtos/responses/quote-result-dto-response';
import { SubscribeRequestDto } from './dtos/requests/subscribe-dto-request';
import { SubscribeResultDto } from './dtos/responses/subscribe-result-dto-response';
import { SubscriptionDto } from './dtos/responses/subscription-dto-response';
import { SubscriptionPlansDto } from './dtos/responses/subscription-plan-dto-response';
import { TapVerifyRequestDto } from './dtos/requests/tap-verify-dto-request';
import { TapVerifyResultDto } from './dtos/responses/tap-verify-result-dto-response';
import { ToolsCatalogDto } from './dtos/responses/tools-catalog-dto-response';
import { TrialResultDto } from './dtos/responses/trial-result-dto-response';

/**
 * SubscriptionApiClient — thin transport for `/api/v3/subscription/*` over
 * {@link AppHttp} (which attaches the v2 bearer + Accept-Language and unwraps the
 * `{ status, message, data }` envelope). No domain mapping and no fallback here —
 * those live in {@link SubscriptionStore} / `subscription.mapper.ts`.
 *
 * One method per LIVE route (verified against the API on 2026-08-04); there are
 * no others under this prefix. `verify` is the one call outside it — the shared
 * `/api/payments/tap/verify` charge check (SUBSCRIPTION-RECURRING R2).
 * `…/plans` is PUBLIC → tagged `anonymous` (no bearer). Everything else carries
 * the v2 token.
 *
 * `silent` is set on the READS that have a graceful recovery path (the store
 * falls back to its cache or hides the section), so a recovered failure never
 * nags the user; the WRITES stay loud because a failed mutation must be seen.
 *
 * LOGGING happens HERE — the earliest point in the feature, before any mapping,
 * fallback or state decision can hide what the wire actually did. Every call
 * emits a `→` line when it is sent and a `←` / `✗` line when it settles, and a
 * failure carries the httpStatus, whether the message came FROM the API, and the
 * raw body. So a bug report only needs the console, and a `silent` read that
 * recovered invisibly is still visible to us.
 */
@Service()
export class SubscriptionApiClient {
  private readonly http = inject(AppHttp);
  private readonly logger: ScopedLogger = inject(LoggerService).forSource('SubscriptionApi');

  /** §4 — PUBLIC list of subscribable plans. Silent: the store keeps the last catalog. */
  plans(): Observable<SubscriptionPlansDto> {
    return this.track(
      'GET /plans',
      this.http.get<SubscriptionPlansDto>('/api/v3/subscription/plans', { anonymous: true, silent: true }),
    );
  }

  /** §5 — current user's subscription state. Silent: the store falls back to its cache. */
  get(): Observable<SubscriptionDto> {
    return this.track('GET /subscription', this.http.get<SubscriptionDto>('/api/v3/subscription', { silent: true }));
  }

  /** §5 — start the free 7-day trial (no body). */
  startTrial(): Observable<TrialResultDto> {
    return this.track('POST /trial', this.http.post<TrialResultDto>('/api/v3/subscription/trial', {}));
  }

  /** §5 — initiate a paid subscription; hands back the hosted-checkout URL. */
  subscribe(body: SubscribeRequestDto): Observable<SubscribeResultDto> {
    return this.track(
      'POST /subscribe',
      this.http.post<SubscribeResultDto>('/api/v3/subscription/subscribe', body),
      body,
    );
  }

  /** §5 — cancel → moves to `cancelled_grace` (keeps access for 30 days). */
  cancel(body: CancelRequestDto = {}): Observable<CancelResultDto> {
    return this.track(
      'POST /cancel',
      this.http.post<CancelResultDto>('/api/v3/subscription/cancel', body),
      { hasReason: !!body.reason },
    );
  }

  /** §5 — reactivate from grace / post-cancellation; hands back a checkout URL. */
  reactivate(body: SubscribeRequestDto): Observable<SubscribeResultDto> {
    return this.track(
      'POST /reactivate',
      this.http.post<SubscribeResultDto>('/api/v3/subscription/reactivate', body),
      body,
    );
  }

  /**
   * Price a plan BEFORE committing: wallet credit + promo discount + net payable.
   * Silent — the checkout summary degrades to the plain plan price when this is
   * unavailable, so the user is never blocked by a quote outage.
   */
  quote(body: SubscribeRequestDto): Observable<QuoteResultDto> {
    return this.track(
      'POST /quote',
      this.http.post<QuoteResultDto>('/api/v3/subscription/quote', body, { silent: true }),
      body,
    );
  }

  /** Turn automatic renewal on/off for the running subscription. */
  autoRenew(body: AutoRenewRequestDto): Observable<AutoRenewResultDto> {
    return this.track(
      'POST /auto-renew',
      this.http.post<AutoRenewResultDto>('/api/v3/subscription/auto-renew', body),
      body,
    );
  }

  /**
   * «تغيير البطاقة» — the server opens a CAPTURED hosted verification charge
   * (refunded in full) and hands back its url; the new card becomes the renewal
   * mandate only once that charge captures. `403 not_premium_active` without an
   * active paid subscription; `400` when the gateway page couldn't be created.
   */
  paymentMethod(body: PaymentMethodRequestDto): Observable<PaymentMethodResultDto> {
    return this.track(
      'POST /payment-method',
      this.http.post<PaymentMethodResultDto>('/api/v3/subscription/payment-method', body),
      body,
    );
  }

  /**
   * Fast-path confirmation of a charge the gateway returned from (activation is
   * still webhook-driven; idempotent per charge id). Silent: the store falls back
   * to re-reading `GET /subscription`, so a failed verify never nags the user.
   */
  verify(body: TapVerifyRequestDto): Observable<TapVerifyResultDto> {
    return this.track(
      'POST /payments/tap/verify',
      this.http.post<TapVerifyResultDto>('/api/payments/tap/verify', body, { silent: true }),
      body,
    );
  }

  /** Billing history. Silent: the billing screen shows its own error/empty state. */
  invoices(): Observable<InvoiceDto[]> {
    return this.track('GET /invoices', this.http.get<InvoiceDto[]>('/api/v3/subscription/invoices', { silent: true }));
  }

  /** Saved payment cards. Silent: the picker simply hides when unavailable. */
  cards(): Observable<CardDto[]> {
    return this.track('GET /cards', this.http.get<CardDto[]>('/api/v3/subscription/cards', { silent: true }));
  }

  /** Khoyek tool catalog — only its size is read (S1 «الأدوات» row). Silent: the row drops the number. */
  tools(): Observable<ToolsCatalogDto> {
    return this.track('GET /tools', this.http.get<ToolsCatalogDto>('/api/v3/tools', { silent: true }));
  }

  // ─── internals ────────────────────────────────────────────────────────
  /**
   * Wrap one call in request/response/error logging. `tap`'s `subscribe` hook
   * fires when the request is actually SENT (these are cold observables — logging
   * in the method body would lie about calls nobody subscribed to).
   *
   * The error line is the important one: it records `httpStatus`, `fromApi`
   * (did the backend explain itself, or is `message` our own per-status
   * default?), the message we will show, and the raw body. That is exactly what
   * the store needs to pick between the server's words and this request's own
   * fallback copy — logged before anything gets a chance to swallow it.
   */
  private track<T>(op: string, request$: Observable<T>, body?: unknown): Observable<T> {
    return request$.pipe(
      tap({
        subscribe: () => this.logger.debug(`→ ${op}`, body ? { body } : undefined),
        next: (data) => this.logger.debug(`← ${op}`, { result: summarize(data) }),
        error: (err: unknown) =>
          this.logger.error(`✗ ${op}`, {
            httpStatus: err instanceof ApiError ? err.httpStatus : null,
            fromApi: err instanceof ApiError ? err.fromApi : false,
            message: err instanceof ApiError ? err.message : String(err),
            raw: err instanceof ApiError ? err.raw : err,
          }),
      }),
    );
  }
}

/**
 * Compact a response for the log line — a count for a list, the payload itself
 * for an object. Keeps the console readable without hiding the shape (which is
 * the thing most worth seeing while the undocumented endpoints settle).
 */
function summarize(data: unknown): unknown {
  return Array.isArray(data) ? { count: data.length, first: data[0] ?? null } : data;
}
